import { Badge } from '@heidi/design-system/badge';
import {
  TypographyCaption,
  TypographyH1,
  TypographyP2Bold,
} from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { cn } from '@heidi/utils';
import Image from 'next/image';
import { Trans } from 'react-i18next';
import avatarFreeToPro from '../../assets/image/free-to-pay/avatar-david-canes.svg';
import startsSvg from '../../assets/image/free-to-pay/trust-pilot-starts.svg';
import { TrustPilotSvg } from '../../assets/image/free-to-pay/trust-pilot-svg';

export interface PaywallDesktopBrandingProps {
  showFreeToTryBadge?: boolean;
  showCancelAnytimeBadge?: boolean;
  testimonial?: {
    text?: string;
    i18nKey?: string;
  };
  showTrustPilotBadge?: boolean;
}

export const PaywallDesktopBranding = ({
  showFreeToTryBadge,
  showCancelAnytimeBadge = true,
  testimonial,
  showTrustPilotBadge = true,
}: PaywallDesktopBrandingProps) => {
  const { t } = useTranslations();

  const defaultTestimonialText = t('upgradeProModal.testimonial');
  const defaultTestimonialI18nKey = 'upgradeProModal.testimonial';
  const defaultDoctorName = 'Dr. David Canes';
  const defaultDoctorTitle = `${t('specialties.Urologist')} • Lahey Hospital & Medical Center`;

  const testimonialText = testimonial?.text || defaultTestimonialText;
  const testimonialI18nKey = testimonial?.i18nKey || defaultTestimonialI18nKey;

  return (
    <section className="hidden flex-col justify-between gap-y-4 p-6 sm:flex sm:w-1/2">
      <div
        className={cn(
          'flex grow flex-col items-start gap-y-8',
          !showFreeToTryBadge && !showCancelAnytimeBadge && 'justify-center'
        )}
      >
        <div className="flex gap-2">
          {showFreeToTryBadge && (
            <Badge color="inverted" size="sm">
              {t('upgradeProModal.freeToTry')}
            </Badge>
          )}
          {showCancelAnytimeBadge && (
            <Badge color="inverted" size="sm">
              {t('upgradeProModal.cancelAnytime')}
            </Badge>
          )}
        </div>

        <>
          <TypographyH1 className="text-lg text-text-primary-invert newBrand:text-text-primary sm:max-w-87.5 sm:text-4xl lg:max-w-full lg:text-5xl">
            {testimonialI18nKey ? (
              <Trans
                i18nKey={testimonialI18nKey}
                components={{
                  br: <br />,
                }}
              >
                {testimonialText}
              </Trans>
            ) : (
              testimonialText
            )}
          </TypographyH1>

          <div className="flex max-w-[400px] items-center gap-3">
            <Image src={avatarFreeToPro} alt="avatar" height={32} width={32} />
            <div className="flex flex-col">
              <TypographyP2Bold className="text-text-primary-invert newBrand:text-text-primary">
                {defaultDoctorName}
              </TypographyP2Bold>
              <TypographyCaption className="text-text-primary-invert newBrand:text-text-primary">
                {defaultDoctorTitle}
              </TypographyCaption>
            </div>
          </div>
        </>
      </div>
      {showTrustPilotBadge && (
        <div className="flex flex-col justify-between gap-y-1">
          <TrustPilotSvg alt="trust-pilot" width={90} height={90} />
          <Image
            src={startsSvg}
            alt="TrustPilot stars ratings"
            width={110}
            height={110}
          />
          <p className="text-[8px] text-text-primary-invert newBrand:text-text-primary">
            {t('upgradeProModal.trustScore')} <b>4.8</b> {t('upgradeProModal.reviews')}
          </p>
        </div>
      )}
    </section>
  );
};
